// src/components/assistant/chatStream.ts
//
// Cliente del chat del panel EN TIEMPO REAL (SSE sobre fetch).
//   POST {base}/threads/:id/messages/stream   (Orión: api/routes/threads_stream.py)
//
// Se usa fetch + ReadableStream (no EventSource): es POST y lleva el JWT en
// Authorization. Protocolo (`data: {json}\n\n`; líneas que empiezan con ":" son
// heartbeats y se ignoran):
//   status  {phase, label}       «Pensando…», «Consultando leads…»
//   delta   {text}               texto incremental de la respuesta
//   reset                        lo transmitido era un preámbulo: bórralo
//   bloque  {bloque}             bloque rico listo (el `done` los trae todos)
//   done    {ok, message, thread, streamed_live}   = cuerpo del endpoint JSON
//   error   {message}
//
// Si el endpoint no existe o algo se interpone ANTES del primer evento (404/405,
// proxy 5xx, respuesta que no es SSE, red caída) → `{ kind: 'fallback' }` y el
// llamador usa el endpoint JSON de siempre, sin que el usuario lo note.

export interface StreamStatusEvent { type: 'status'; phase?: string; label?: string }
export interface StreamDeltaEvent { type: 'delta'; text: string }
export interface StreamResetEvent { type: 'reset' }
export interface StreamBloqueEvent { type: 'bloque'; bloque: { tipo: string; contenido: string } }
export interface StreamDoneEvent {
  type: 'done';
  ok?: boolean;
  message?: { id?: string; role?: string; bloques?: Array<{ tipo: string; contenido: string }>; thinking?: string | null };
  thread?: { chat_model?: unknown; model_used?: string | null; title_pending?: boolean };
  streamed_live?: boolean;
}
export interface StreamErrorEvent { type: 'error'; message?: string }
export type StreamEvent =
  | StreamStatusEvent
  | StreamDeltaEvent
  | StreamResetEvent
  | StreamBloqueEvent
  | StreamDoneEvent
  | StreamErrorEvent;

export type StreamOutcome =
  | { kind: 'done'; done: StreamDoneEvent }
  | { kind: 'fallback'; reason: string }
  | { kind: 'aborted' };

/** Error HTTP del endpoint (401/403/409/422…) con el `detail` del backend. */
export class StreamHttpError extends Error {
  constructor(public status: number, public detail: string | null) {
    super(detail || `Error al enviar (${status})`);
  }
}

// Estados en los que el stream "no existe" o un intermediario lo rompió antes
// de que el backend aceptara el turno → usar el endpoint JSON.
const FALLBACK_STATUS = new Set([404, 405, 501, 502, 503, 504]);

/** Parte un búfer SSE en eventos completos; devuelve [eventos, resto]. */
export function parseSseChunk(buffer: string): [StreamEvent[], string] {
  const out: StreamEvent[] = [];
  const norm = buffer.replace(/\r\n/g, '\n');
  const parts = norm.split('\n\n');
  const rest = parts.pop() ?? '';
  for (const part of parts) {
    const data = part
      .split('\n')
      .filter((l) => l.startsWith('data:'))
      .map((l) => l.slice(5).replace(/^ /, ''))
      .join('\n');
    if (!data) continue; // heartbeat (": ping") o línea vacía
    try {
      const ev = JSON.parse(data);
      if (ev && typeof ev.type === 'string') out.push(ev as StreamEvent);
    } catch {
      /* evento malformado: se ignora */
    }
  }
  return [out, rest];
}

async function readDetail(res: Response): Promise<string | null> {
  try {
    const j = await res.json();
    return typeof j?.detail === 'string' ? j.detail : null;
  } catch {
    return null;
  }
}

export async function streamThreadMessage(opts: {
  url: string;
  headers: Record<string, string>;
  body: unknown;
  signal: AbortSignal;
  onEvent: (ev: StreamEvent) => void;
}): Promise<StreamOutcome> {
  const { url, headers, body, signal, onEvent } = opts;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { ...headers, Accept: 'text/event-stream' },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if (signal.aborted) return { kind: 'aborted' };
    return { kind: 'fallback', reason: `red: ${e instanceof Error ? e.message : String(e)}` };
  }

  if (!res.ok) {
    if (FALLBACK_STATUS.has(res.status)) return { kind: 'fallback', reason: `HTTP ${res.status}` };
    throw new StreamHttpError(res.status, await readDetail(res));
  }
  const ctype = res.headers.get('content-type') || '';
  if (!ctype.includes('text/event-stream') || !res.body) {
    return { kind: 'fallback', reason: `no es SSE (${ctype || 'sin content-type'})` };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let gotEvent = false;
  let done: StreamDoneEvent | null = null;
  try {
    for (;;) {
      const { value, done: finished } = await reader.read();
      if (finished) break;
      buffer += decoder.decode(value, { stream: true });
      const [events, rest] = parseSseChunk(buffer);
      buffer = rest;
      for (const ev of events) {
        gotEvent = true;
        if (ev.type === 'done') done = ev;
        else if (ev.type === 'error') throw new Error(ev.message || 'No se pudo completar la respuesta.');
        else onEvent(ev);
      }
      if (done) break;
    }
  } catch (e) {
    if (signal.aborted) return { kind: 'aborted' };
    // Red cortada antes de cualquier evento: el turno no llegó a empezar.
    if (!gotEvent && e instanceof TypeError) return { kind: 'fallback', reason: `stream: ${e.message}` };
    throw e;
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* noop */
    }
  }
  if (done) return { kind: 'done', done };
  if (signal.aborted) return { kind: 'aborted' };
  if (!gotEvent) return { kind: 'fallback', reason: 'stream vacío' };
  throw new Error('La conexión se interrumpió antes de terminar la respuesta. Intenta de nuevo.');
}
